import { Defaults } from "@common/_constants";
import { Button } from "@common/components/Atoms/Button";
import Error from "@common/components/Atoms/Error";
import {
    FormHandler,
    Rules,
    Select,
    TextArea,
} from "@common/components/Atoms/FormInputs";
import Loading from "@common/components/Atoms/Loading";
import Modal from "@common/components/Molecules/Modal";
import { FormField } from "@common/models";
import { JobService, ReferralService } from "@pro/_services";
import React from "react";

interface DeclineReasonModel {
    value: string;
    label: string;
}

interface DeclinePositionState {
    isOpen: boolean;
    jobCandidateId?: number;
    loading: boolean;
    error: boolean;
    errorMessages: string[];
    formIsValid: boolean;
    reasonField: FormField;
    notesField: FormField;
    declineReasons: DeclineReasonModel[];
}

interface DeclinePositionProps {
    isOpen: boolean;
    onOkay: () => void;
    onCancel: () => void;
    closeModal: () => void;
    jobCandidateId: number;
    jobName: string;
    jobCandidateName: string;
}

export class DeclinePosition extends React.Component<DeclinePositionProps, DeclinePositionState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    constructor(props) {
        super(props);

        this.getEmptyState = this.getEmptyState.bind(this);
        this.declineJob = this.declineJob.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onReasonChange = this.onReasonChange.bind(this);
        this.onReasonNoteChange = this.onReasonNoteChange.bind(this);

        this.state = this.getEmptyState();

        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.declineReasons) {
            this._abortControllers.declineReasons.abort();
        }
        this._abortControllers.declineReasons = new AbortController();
        const signal = this._abortControllers.declineReasons.signal;

        JobService.declineReasons(signal).then(data => {
            this.setState({ declineReasons: data });
        });
    }

    componentWillReceiveProps(newProps) {
        if (newProps.isOpen && newProps.isOpen !== this.props.isOpen) {
            this.setState(this.getEmptyState());
        }
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key => abortControllers[key].abort());
        }
    }

    getEmptyState(): DeclinePositionState {
        const state: Partial<DeclinePositionState> = {
            loading: false,
            error: false,
            errorMessages: [],
            formIsValid: true,
            reasonField: {
                name: "reason",
                label: "",
                value: "",
                isValid: true,
                validationMessages: [] as string[],
                rules: [Rules.required],
            },
            notesField: {
                name: "notes",
                value: "",
                label: "",
                isValid: true,
                validationMessages: [] as string[],
                rules: [Rules.required],
            },
            isOpen: false,
        };

        return state as DeclinePositionState;
    }

    onReasonChange(name, newReason) {
        const reasonField = this.state.reasonField;
        reasonField.value = newReason;
        this.setState({ reasonField });
    }

    onReasonNoteChange(name, newNotes) {
        const notesField = this.state.notesField;
        notesField.value = newNotes;
        this.setState({ notesField });
    }

    onSubmit(event) {
        event.preventDefault();

        const reasonField = this.state.reasonField;
        const notesField = this.state.notesField;
        const isValid = FormHandler.validate({ reasonField, notesField });

        if (isValid) {
            this.setState({ reasonField, notesField, loading: true, formIsValid: true }, () =>
                this.declineJob(
                    this.props.jobCandidateId,
                    this.state.notesField.value,
                    this.state.reasonField.value
                ).then(() => {
                    this.setState({ loading: false }, this.props.onOkay);
                })
            );
        } else {
            this.setState({ reasonField, notesField, formIsValid: false });
        }
    }

    declineJob(jobCandidateId, notes, reason) {
        return ReferralService.declineJob(jobCandidateId, notes, reason)
            .then(this.props.onOkay)
            .catch(err => console.log(err));
    }

    render() {
        if (this.props.isOpen && this.state.loading) {
            return (
                <Modal
                    title={this.props.jobName}
                    isOpen={this.props.isOpen}
                    closeModal={this.props.closeModal}>
                    <Loading />
                </Modal>
            );
        }

        return (
            <Modal
                title="decline position"
                isOpen={this.props.isOpen}
                closeModal={this.props.closeModal}>
                <div className="c-block">
                    <p>
                        Would you like <strong>{this.props.jobCandidateName}</strong> to decline the
                        job, <strong>{this.props.jobName}</strong>?
                    </p>
                </div>

                <form onSubmit={this.onSubmit}>
                    <Select
                        name="reason"
                        label="reason for declining"
                        value={this.state.reasonField.value}
                        isValid={this.state.reasonField.isValid}
                        validationMessages={this.state.reasonField.validationMessages}
                        onChange={this.onReasonChange}
                        multi={false}
                        options={this.state.declineReasons}
                    />

                    <TextArea
                        name="notes"
                        autoComplete="Off"
                        readOnly={false}
                        required={true}
                        label="details about the declining (internal note)"
                        value={this.state.notesField.value}
                        maxLength={10239}
                        isValid={this.state.notesField.isValid}
                        validationMessages={this.state.notesField.validationMessages}
                        onChange={this.onReasonNoteChange}
                    />

                    <div className="c-block">
                        <Error
                            show={!this.state.formIsValid}
                            description={Defaults.FormInvalidMessage}
                        />
                    </div>
                    <div className="c-modal__footer">
                        <div className="c-modal__footer-left">
                            <Button
                                id="decline-position-button"
                                type="submit"
                                green={true}
                                onClick={this.onSubmit}>
                                decline
                            </Button>
                        </div>
                        <div className="c-modal__footer-right">
                            <Button
                                id="cancel-position-button"
                                type="button"
                                onClick={this.props.onCancel}>
                                cancel
                            </Button>
                        </div>
                    </div>
                </form>
            </Modal>
        );
    }
}
